import React, { useState, useMemo } from 'react';
import { CellText, DatePicker } from '@opendate-app/setlist';
import ReportingGiftCardsReportPage, { formatCents, centsKpiCard, countKpiCard } from './ReportingGiftCardsReportPage';
import {
  GIFT_CARD_LIABILITY_COLUMNS, SORT_TYPES, defaultVisibleColumns, defaultHiddenColumns,
} from '../constants/giftCardsColumnsConfig';
import { formatYMD } from '../utils/reportingUtils';
import '../styles/giftCardReports.css';

const LIABILITY_CONFIG = {
  pageTitle: 'Gift Card Liability',
  pageSubtitle: 'Money left on your gift cards at the end of the chosen day, by product and where the card works, split into value buyers paid for and promotional value (free and bonus cards, staff top-ups), with how long the balances have been outstanding. Expired cards are shown separately and are not part of the liability. Compared with the same day a year earlier.',
  sidebarKey: 'gift_cards_liability',
  defaultPreset: 'year_to_date',
  showDatePicker: false,
  dateModeOptions: [{ key: 'balance_date', label: 'Balance Date' }],
  defaultSortRules: [{ field: 'outstanding_cents', direction: 'desc' }],

  columns: GIFT_CARD_LIABILITY_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns: defaultVisibleColumns(GIFT_CARD_LIABILITY_COLUMNS),
  getDefaultHiddenColumns: defaultHiddenColumns(GIFT_CARD_LIABILITY_COLUMNS),
  localStorageKey: (teamId) => `reportingGiftCardsLiabilityColumns_${teamId}`,
  firstColumnId: 'product_name',

  detailErrorMessage: 'Failed to load gift card liability.',
  detailTableTitle: 'Outstanding Balances',
  emptyMessage: 'No gift card balances at this date.',
  searchPlaceholder: 'Search products...',

  kpiGridColumns: 3,
  buildKpiCards: (current, prior) => [
    centsKpiCard('Outstanding Liability', 'outstanding_cents', current, prior),
    centsKpiCard('Paid Balances', 'paid_cents', current, prior),
    centsKpiCard('Promotional Balances', 'promotional_cents', current, prior),
    countKpiCard('Cards With Balance', 'cards_with_balance', current, prior),
    centsKpiCard('Average Balance', 'average_balance_cents', current, prior),
    centsKpiCard('Expired Unused', 'expired_cents', current, prior),
  ],

  renderCellValue: (col, row) => {
    switch (col.id) {
      case 'product_name': return row.product_name;
      case 'scope_label': return row.scope_label;
      case 'cards': return (row.cards || 0).toLocaleString();
      case 'outstanding_cents': return <CellText primary={formatCents(row.outstanding_cents)} />;
      default: return formatCents(row[col.id]);
    }
  },
};

const AS_OF_LABEL = 'vs same day last year';

function ReportingGiftCardsLiabilityPage(props) {
  const [asOf, setAsOf] = useState(null);
  const detailParams = useMemo(() => (asOf ? { as_of: asOf } : {}), [asOf]);

  const config = {
    ...LIABILITY_CONFIG,
    comparisonLabel: AS_OF_LABEL,
    renderToolbarExtras: () => (
      <DatePicker
        className="gift-card-as-of-picker"
        size="sm"
        placeholder="Balances as of today"
        value={asOf ? new Date(`${asOf}T00:00:00`) : null}
        max={new Date()}
        onChange={(date) => setAsOf(date instanceof Date && !Number.isNaN(date.getTime()) ? formatYMD(date) : null)}
      />
    ),
  };

  return (
    <ReportingGiftCardsReportPage
      {...props}
      report="liability"
      filterIds={['gift_card_product_id', 'scope', 'channel']}
      config={config}
      detailParams={detailParams}
    />
  );
}

export default ReportingGiftCardsLiabilityPage;
